<template>
  <div>
    <p
      v-if="!hideTitle"
      class="ma-0 pa-0 subtitle-2 blue-grey--text text--darken-3 mb-6"
      data-testid="checklistOverview-text-title"
    >
      {{ $t('checklist.checklist_one') }}
    </p>
    <template v-for="{ item, key } in checklistItemsWithKeys">
      <div :key="key" v-if="item.type === 'separator'">
        <p
          class="subtitle-2 ma-0 pa-0 mb-2 mt-4 grey--text text--darken-1 d-flex align-center"
          :data-testid="`checklistOverview-text-sectionTitle-${key}`"
        >
          <v-tooltip max-width="400px" top v-if="hasVisibilityRule(item)">
            <template v-slot:activator="{ on }">
              <v-icon
                class="me-1"
                color="primary"
                data-testid="checklistOverview-icon-visibilityRule"
                size="18"
                v-on="on"
              >
                mdi-eye-outline
              </v-icon>
            </template>
            <span data-testid="checklistOverview-tooltip-visibilityRule">
              {{ getVisibilityRuleTooltip(item) }}
            </span>
          </v-tooltip>

          <template v-if="shouldShowSectionWeight(item)">
            {{ item.name }} -&nbsp;
            <span
              class="font-weight-bold text-wrap-anywhere"
              data-testid="checklistOverview-text-sectionWeight"
            >
              {{ $t('routines.scoring.weight_label') }}:
              {{ formatScorePercentage(item.scoring.weightPercentage) }}%
            </span>
          </template>

          <template v-else>
            <span class="text-wrap-anywhere">{{ item.name }}</span>
          </template>
        </p>
      </div>

      <div
        v-else
        class="d-flex align-center justify-start ms-2 mb-3"
        :key="key"
      >
        <v-icon color="blue-grey darken-1" class="me-2" size="18">
          mdi-circle-medium
        </v-icon>
        <p
          class="ma-0 pa-0 body-2 blue-grey--text text--darken-1 text-wrap-anywhere d-flex align-center"
        >
          <v-tooltip max-width="400px" top v-if="hasVisibilityRule(item)">
            <template v-slot:activator="{ on }">
              <v-icon
                class="me-1"
                color="primary"
                data-testid="checklistOverview-icon-visibilityRule"
                size="18"
                v-on="on"
              >
                mdi-eye-outline
              </v-icon>
            </template>
            <span data-testid="checklistOverview-tooltip-visibilityRule">
              {{ getVisibilityRuleTooltip(item) }}
            </span>
          </v-tooltip>

          {{ item.name }}
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import { utils } from 'frontend-js-shared'

import { formatScorePercentage } from '@/lib/formatScorePercentage'

const ITEM_TYPES = Object.freeze({
  SEPARATOR: 'separator',
})

export default {
  name: 'ChecklistOverview',

  props: {
    checklist: {
      type: Object,
      default: () => ({}),
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    isSectionScoring() {
      return this.checklist?.scoringType === 'section'
    },

    checklistItems() {
      return this.checklist?.items || []
    },

    checklistItemsWithKeys() {
      return this.checklistItems.map((item, index) => ({
        item,
        key:
          utils.getEntityId(item) ||
          `${
            item?.type === ITEM_TYPES.SEPARATOR ? 'separator' : 'item'
          }-${index}`,
      }))
    },
  },

  methods: {
    formatScorePercentage,

    shouldShowSectionWeight(item) {
      return (
        this.isSectionScoring &&
        ![null, undefined, ''].includes(item?.scoring?.weightPercentage)
      )
    },

    hasVisibilityRule(item) {
      return utils.checklists.hasVisibilityRule(item)
    },

    getTriggerQuestionName(item) {
      const triggerNames = (item?.visibilityRules ?? [])
        .map(rule => utils.getEntityId(rule?.triggerItemId))
        .map(triggerItemId =>
          this.checklistItems.find(
            candidate => utils.getEntityId(candidate) === triggerItemId
          )
        )
        .map(trigger => trigger?.name?.trim())
        .map(name => name || this.$t('checklist.question'))

      return [...new Set(triggerNames)].join(', ')
    },

    getVisibilityRuleTooltip(item) {
      return this.$t('checklist.visibility_rule.overview_tooltip', {
        triggerQuestionTitle: this.getTriggerQuestionName(item),
        interpolation: { escapeValue: false },
      })
    },
  },
}
</script>
